/**
 * The clinch: two fighters in one state.
 *
 * Everything else in this game happens to one fighter at a time. A grapple does
 * not — it is a **paired state**, one holding and one held, and for as long as
 * it lasts the two of them are a single thing with a fixed shape. That shape is
 * data: `partnerOffset` in the move, in the holder's own frame, and this module
 * writes the held fighter onto it every frame.
 *
 * Which means the position of a held fighter is not theirs any more. Nothing
 * else may write to it while the hold lasts, and nothing does: `grappled` is not
 * a locomotion state, so `locomotion` refuses to move them, and this module runs
 * after knockback so a shove cannot drag them off the mark.
 *
 * The way out is a **timing window**, not a button. It opens some frames into
 * the hold and shuts again, and pressing before it opens spends the attempt —
 * otherwise the answer to every grapple is to mash, and a window you cannot
 * fail is not a window.
 *
 * Pure. It holds no three.js: a fighter here is anything with a position, a
 * facing, a state machine and a `faceTowards`.
 */

export const OUTCOMES = Object.freeze(['completed', 'escaped', 'broken']);

export function createGrapple({ frameRate = 60, onResolve } = {}) {
  const listeners = new Set();
  if (onResolve) listeners.add(onResolve);

  let hold = null;

  function seconds(frames) {
    return frames / frameRate;
  }

  /**
   * Where the held fighter stands, in the world.
   *
   * The offset is in the holder's frame, and the rig's forward is its own -Z —
   * the same convention the strike clip drives the fist down — so a
   * `partnerOffset` of `[0, 0, -0.62]` means 0.62m in front of them.
   */
  function markFor(holder, offset) {
    const forward = holder.facing;
    const right = { x: -forward.z, z: forward.x };
    return {
      x: holder.position.x + right.x * offset[0] + forward.x * -offset[2],
      z: holder.position.z + right.z * offset[0] + forward.z * -offset[2],
    };
  }

  function place() {
    if (!hold) return;
    const { holder, held, move } = hold;
    const mark = markFor(holder, move.partnerOffset);
    held.position.x = mark.x;
    held.position.z = mark.z;
    // The held fighter is turned to their holder. The holder keeps the facing
    // they committed to when the clinch landed — `grappling` does not declare
    // `faces`, so nothing else turns them either.
    held.faceTowards?.(holder.position);
  }

  /**
   * Ends the hold and lets both fighters go.
   *
   * Releasing them to `idle` here is a floor, not a decision: *what* a resolved
   * grapple is worth belongs to whoever is listening, and they can request
   * something else the moment they hear about it. But a pair left standing in
   * `grappling` and `grappled` with nothing holding them there would be stuck
   * for the rest of the round, and this module is the only thing that knows the
   * hold is over.
   *
   * A fighter already out of their paired state is left alone — whatever pulled
   * them out is the reason this is being called.
   */
  function resolve(outcome, extra = {}) {
    if (!hold) return null;
    const finished = { ...hold, outcome, ...extra };
    const { holder, held, move } = hold;
    hold = null;

    if (holder.machine.state === move.holdState) {
      holder.machine.request('idle', { ignoreInterrupt: true });
    }
    // A whipped fighter is not let go, they are *sent*. Releasing them to idle
    // first would put them through a state they are never in, and idle is a
    // locomotion state — for one frame they could walk out of their own throw.
    if (outcome !== 'whipped' && held.machine.state === move.partnerState) {
      held.machine.request('idle', { ignoreInterrupt: true });
    }

    for (const listener of listeners) listener(finished);
    return finished;
  }

  return {
    get active() {
      return hold !== null;
    },
    get holder() {
      return hold?.holder ?? null;
    },
    get held() {
      return hold?.held ?? null;
    },
    get move() {
      return hold?.move ?? null;
    },
    get elapsed() {
      return hold?.elapsed ?? 0;
    },
    /** Seconds until the hold runs out on its own. */
    get remaining() {
      return hold ? Math.max(0, seconds(hold.move.hold) - hold.elapsed) : 0;
    },
    /** True while the held fighter's escape input would work. */
    get escapeOpen() {
      if (!hold || hold.spent || !hold.move.escape) return false;
      const { open, frames } = hold.move.escape;
      return hold.elapsed >= seconds(open) && hold.elapsed < seconds(open + frames);
    },
    /** True for a hold with no way out at all. */
    get inescapable() {
      return Boolean(hold) && !hold.move.escape;
    },
    /** True once the held fighter has thrown their attempt away. */
    get escapeSpent() {
      return Boolean(hold?.spent);
    },

    /** Whether this fighter is the one being held right now. */
    isHeld: (fighter) => hold?.held === fighter,
    isHolding: (fighter) => hold?.holder === fighter,

    /**
     * Starts a hold. Refuses to start one on top of another — a clinch is
     * exclusive, and two at once would have two writers on one position.
     */
    begin(holder, held, move, name = null) {
      if (hold || !holder || !held || holder === held) return false;
      if (!holder.machine.request(move.holdState, { ignoreInterrupt: true })) return false;
      if (!held.machine.request(move.partnerState, { ignoreInterrupt: true })) {
        // The holder is already committed; put them back rather than leaving
        // them holding nobody.
        holder.machine.request('idle', { ignoreInterrupt: true });
        return false;
      }
      // `name` is the key the move was looked up under, carried through to
      // whoever resolves the hold. The move object does not know its own key,
      // and the blow that lands at the end of a hold is read as one by the
      // combo layer — a display name in that field put "Wall Slam" into a
      // fighter's chain, where the next follow-up looked it up and found
      // nothing. See gotcha 38.
      hold = { holder, held, move, name, elapsed: 0, spent: false };
      place();
      return true;
    },

    /**
     * The held fighter's attempt to break out.
     *
     * Returns what happened, which is the whole mechanic: `early` spends the
     * attempt and buys nothing, `broken` gets them out, and there is no third
     * chance — a fighter who guesses wrong is held to the end.
     */
    tryEscape(fighter) {
      if (!hold || (fighter && hold.held !== fighter)) return 'none';
      // Nothing to time, because there is nothing to hit. A fighter who is
      // still seeing stars does not slip a hold.
      if (!hold.move.escape) return 'inescapable';
      if (hold.spent) return 'spent';
      if (!this.escapeOpen) {
        // Too early, or too late. Either way the attempt is gone.
        hold.spent = true;
        return 'early';
      }
      resolve('escaped');
      return 'broken';
    },

    /**
     * The Irish whip: the holder lets go *along a direction* instead of
     * finishing the hold.
     *
     * A throw is not a move with a window — there is nothing to time and
     * nothing to beat, because the fighter is already held. All the holder
     * chooses is where, which is why a direction is the whole input. What it
     * costs them is the damage the hold would have paid out: throwing somebody
     * is trading a certain twelve points for wherever the arena happens to be.
     */
    whip(direction) {
      if (!hold) return null;
      const length = Math.hypot(direction?.x ?? 0, direction?.z ?? 0);
      if (!(length > 1e-4)) return null;
      return resolve('whipped', {
        direction: { x: direction.x / length, z: direction.z / length },
      });
    },

    /**
     * Ends a hold that neither fighter finished — the holder knocked out of it,
     * a knockout, a round reset.
     */
    breakOff() {
      return resolve('broken');
    },

    update(dt) {
      if (!hold || !(dt > 0)) return null;
      hold.elapsed += dt;
      // Positions first, so the pair is on its mark before anybody is posed.
      place();

      // A holder dragged out of `grappling` by anything at all is no longer
      // holding anybody, whatever the clock says.
      if (hold.holder.machine.state !== hold.move.holdState) return resolve('broken');
      if (hold.held.machine.state !== hold.move.partnerState) return resolve('broken');

      if (hold.elapsed >= seconds(hold.move.hold)) return resolve('completed');
      return null;
    },

    onResolve(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
